import React from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Chip from '@material-ui/core/Chip';
import Avatar from '@material-ui/core/Avatar';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Badge from '@material-ui/core/Badge';
import Tooltip from '@material-ui/core/Tooltip';
import Select from '@material-ui/core/Select';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import NotificationsIcon from '@material-ui/icons/Notifications';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import CloseIcon from '@material-ui/icons/Close';
import RemoveIcon from '@material-ui/icons/Remove';
import VideoCallIcon from '@material-ui/icons/VideoCall';
import EventIcon from '@material-ui/icons/Event';
import EventBusyIcon from '@material-ui/icons/EventBusy';
import PersonIcon from '@material-ui/icons/Person';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import SendIcon from '@material-ui/icons/Send';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Autocomplete from '@material-ui/lab/Autocomplete';
import DialogActions from '@material-ui/core/DialogActions';
import Divider from '@material-ui/core/Divider';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import logo5 from '../images/logo5.png';
import logo7 from '../images/logo7.png';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { ChatSystemContainer } from '../../components/ChatSystem';
import PatientVideoCallModal from '../../components/VideoCall/PatientVideoCallModal';

const ACTIVE_VIDEO_CALL_SESSION_KEY = 'virtus.videoCall.active';
const PATIENT_HEADER_CACHE_TTL_MS = 30000;
const patientHeaderRequestCache = new Map();

const fetchPatientHeaderData = (cacheKey, url, token, forceRefresh = false) => {
  const cached = patientHeaderRequestCache.get(cacheKey);
  if (!forceRefresh && cached && cached.expiresAt > Date.now()) {
    return cached.promise;
  }

  const promise = fetch(url, {
    headers: { 'Authorization': `Token ${token}` }
  }).then(async (response) => {
    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }
    return response.json();
  });

  patientHeaderRequestCache.set(cacheKey, {
    promise,
    expiresAt: Date.now() + PATIENT_HEADER_CACHE_TTL_MS,
  });
  promise.catch(() => patientHeaderRequestCache.delete(cacheKey));
  return promise;
};

const isVideoCallAlreadyActive = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.sessionStorage.getItem(ACTIVE_VIDEO_CALL_SESSION_KEY) === '1';
  } catch (error) {
    return false;
  }
};

const warnIfVideoCallAlreadyActive = () => {
  if (!isVideoCallAlreadyActive()) {
    return false;
  }

  window.alert('A videocall is already open. Close the current call before starting another one.');
  return true;
};

// Brand Colors
const COLORS = {
  PRIMARY: '#002D55',
  SECONDARY: '#324158',
  ACCENT: '#fb741a',
  BACKGROUND: '#dce4eb',
  TEXT_PRIMARY: '#002D55',
  TEXT_SECONDARY: '#324158',
  TEXT_TERTIARY: '#666666',
  DIVIDER: '#dce4eb',
  BORDER: '#e0e0e0',
  PLACEHOLDER: '#999999',
  ERROR: '#d32f2f',
  SUCCESS: '#4caf50',
};

const inlineStyles = {
  orangeFocused: {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#F79500',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: COLORS.PRIMARY,
    },
  },
  headerWithBorder: {
    background: COLORS.PRIMARY,
    color: '#fff',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    borderBottom: `3px solid ${COLORS.ACCENT}`,
  },
  headerIconButton: {
    color: '#fff',
    padding: '  4px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 165, 0, 0.15)',
      borderColor: COLORS.ACCENT,
    },
  },
  orangeFocused: {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#002D55', // brandcolor blue
    },
    '& .MuiInputLabel-outlined': {
      color: '#002D55', // brandcolor blue (default, not focused)
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27', // hover effect
    },
  },
  responsiveText: {
    fontSize: 'clamp(0.755rem, 1.26vw, 0.95rem)'
  },
  responsiveText2: {
    fontSize: 'clamp(0.655rem, 1.06vw, 0.75rem)'
  },
};

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  appBar: {
    backgroundColor: '#FFFFFF',
    color: '#002D55',
    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
    flexDirection: 'column',
    height: 50,
    maxHeight: 50,
    [theme.breakpoints.down('sm')]: {
      height: 48,
      maxHeight: 48,
    },
    [theme.breakpoints.up('xl')]: {
      height: 57,
      maxHeight: 57,
    },
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    paddingLeft: '0 !important',
    paddingRight: theme.spacing(2),
    margin: 0,
    marginBottom: '0 !important',
    minHeight: '50px !important',
    maxHeight: 50,
    minWidth: 0,
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: '48px !important',
      maxHeight: 48,
      paddingRight: theme.spacing(1),
    },
    [theme.breakpoints.up('xl')]: {
      minHeight: '57px !important',
      maxHeight: 57,
    },
  },
  responsiveLogo: {
    marginLeft: -12,
    marginTop: 0,
    paddingTop: 0,
    paddingBottom: 4,
    marginRight: 12,
    maxWidth: 30,
    minWidth: 30,
    [theme.breakpoints.down('xs')]: {
      marginLeft: -6,
      maxWidth: 28,
      minWidth: 28,
    },
    [theme.breakpoints.up('sm')]: {
      maxWidth: 30,
      minWidth: 30,
    },
    [theme.breakpoints.up('md')]: {
      maxWidth: 30,
      minWidth: 30,
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: 34,
      minWidth: 34,
    },
    [theme.breakpoints.up('xl')]: {
      maxWidth: 38,
      minWidth: 38,
    },
  },
  logo: {
    height: 32,
    objectFit: 'contain',
  },
  spacer: {
    flex: 1,
  },
  smallIconButton: {
    padding: 4,
    marginLeft: 6,
    [theme.breakpoints.down('sm')]: {
      padding: 3,
      marginLeft: 4,
    },
    [theme.breakpoints.up('xl')]: {
      padding: 8,
    },
  },
  smallAvatar: {
    width: theme.spacing(2.4),
    height: theme.spacing(2.4),
    '& .MuiSvgIcon-root': {
      fontSize: 16,
    },
    [theme.breakpoints.down('sm')]: {
      width: theme.spacing(2.2),
      height: theme.spacing(2.2),
    },
    [theme.breakpoints.up('xl')]: {
      width: theme.spacing(4),
      height: theme.spacing(4),
      '& .MuiSvgIcon-root': {
        fontSize: 22,
      },
    },
  },
  headerIcon: {
    fontSize: 18,
    [theme.breakpoints.up('xl')]: {
      fontSize: 22,
    },
  },
  notificationIconButton: {
    marginLeft: 6,
    padding: 2,
    color: '#f79400',
    [theme.breakpoints.up('xl')]: {
      padding: 10,
    },
  },
  messageIconButton: {
    marginLeft: 4,
    padding: 3,
    color: '#f79400',
    [theme.breakpoints.up('xl')]: {
      padding: 6,
    },
  },
  customBadge: {
    backgroundColor: '#fb741a',
    color: '#fff',
    boxShadow: '0 0 0 2px rgba(255,255,255,0.6)',
    fontSize: 'clamp(9px, 0.65vw, 11px)',
    minWidth: 'clamp(14px, 1vw, 18px)',
    height: 'clamp(14px, 1vw, 18px)',
    padding: 0,
  },
  chatContainer: {
    position: 'fixed',
    bottom: 20,
    right: 20,
    zIndex: 1300,
  },
  chatPaper: {
    width: 320,
    borderRadius: 14,
    overflow: 'hidden',
  },
  chatHeader: {
    background: '#002D55',
    color: '#fff',
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  chatMessages: {
    maxHeight: 260,
    overflowY: 'auto',
    padding: '12px',
    background: '#f5f7fa',
  },
  chatInputArea: {
    padding: 8,
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    borderTop: '1px solid #eee',
    background: '#fff',
  },
  minimizedChat: {
    width: 200,
    maxWidth: 220,
    borderRadius: 12,
    padding: '4px 12px',
    background: '#002D55',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    transition: 'box-shadow 120ms ease, transform 120ms ease',
    '&:hover': {
      boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
      transform: 'translateY(-2px)'
    },
    '&:hover $minimizedClose': {
      opacity: 1,
    }
  },
  minimizedClose: {
    opacity: 0,
    transition: 'opacity 160ms ease',
    color: '#fff',
    padding: 6,
  },
  minimizedTitle: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 180,
    cursor: 'pointer',
  },
  chip: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    fontWeight: 600,
    height: 26,
    maxWidth: '42vw',
    minWidth: 0,
    [theme.breakpoints.up('xl')]: {
      height: 32,
    },
    '& .MuiChip-label': {
      fontSize: 'clamp(0.6rem, 0.68vw, 0.75rem)',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '& .MuiChip-icon': {
      fontSize: 'clamp(14px, 0.9vw, 18px)',
    },
  },
  avatarButton: {
    marginLeft: theme.spacing(2),
  },
  treatmentSelect: {
    flexShrink: 0,
    minWidth: 'clamp(100px, calc(44px + 4.167vw), 150px)',
    minHeight: 'clamp(24px, calc(8px + 1.25vw), 32px)',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    margin: '0 !important',
    backgroundColor: '#FFF',
    borderRadius: 18,
    overflow: 'hidden',
    '& .MuiFormControl-marginDense': {
      margin: '0 !important',
    },
    '& .MuiOutlinedInput-root': {
      margin: '0 !important',
      padding: '0 !important',
      borderRadius: 18,
      overflow: 'hidden',
    },
    '& .MuiOutlinedInput-input': {
      paddingTop: 'clamp(2px, calc(-0.5px + 0.2906vw), 6px) !important',
      paddingBottom: 'clamp(2px, calc(-0.5px + 0.2906vw), 6px) !important',
      paddingLeft: 'clamp(7.06px, calc(0px + 1.4967vw), 9.41px) !important',
      paddingRight: '30px !important',
      lineHeight: 1.4,
    },
    '& .MuiSelect-select': {
      fontSize: 'clamp(0.66rem, calc(0.291rem + 0.549vw), 0.95rem)',
      paddingTop: 'clamp(2px, calc(-0.5px + 0.2906vw), 6px)',
      paddingBottom: 'clamp(2px, calc(-0.5px + 0.2906vw), 6px)',
      paddingLeft: 'clamp(7.06px, calc(0px + 1.4967vw), 9.41px)',
      paddingRight: 30,
      lineHeight: 1.4,
      color: '#002D55',
      fontWeight: 500,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#999',
      borderRadius: 18,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: 92,
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      '& .MuiSelect-select': {
        fontSize: '0.72rem',
      },
      '& .MuiOutlinedInput-input': {
        paddingRight: '24px !important',
      },
    },
    [theme.breakpoints.down('xs')]: {
      minWidth: 84,
      '& .MuiSelect-select': {
        fontSize: '0.68rem',
      },
    },
  },
  menuPaper: {
    minWidth: 'clamp(120px, 10vw, 160px)',
    borderRadius: 18,
    marginTop: 8,
    [theme.breakpoints.up('xl')]: {
      minWidth: 180,
    },
  },
  notificationsMenuHeader: {
    padding: '8px 12px 6px 12px',
    borderBottom: '1px solid #eee',
    fontWeight: 600,
    fontSize: 'clamp(0.72rem, 0.8vw, 0.875rem)',
    [theme.breakpoints.up('xl')]: {
      padding: '10px 14px 8px 14px',
      fontSize: '0.875rem',
    },
  },
  dropdownMenuItem: {
    fontSize: 'clamp(0.72rem, 0.8vw, 0.875rem)',
    padding: '6px 16px',
    [theme.breakpoints.up('xl')]: {
      fontSize: '0.875rem',
      padding: '8px 20px',
    },
  },
  treatmentMenuPaper: {
    marginTop: 2,
  },
  select: {
    minWidth: 'clamp(100px, calc(44px + 4.167vw), 150px)',
    minHeight: 'clamp(24px, calc(8px + 1.25vw), 32px)',
    '& .MuiSelect-select': {
      fontSize: 'clamp(0.66rem, calc(0.291rem + 0.549vw), 0.95rem)',
      paddingTop: 'clamp(2px, calc(-0.5px + 0.2906vw), 6px)',
      paddingBottom: 'clamp(2px, calc(-0.5px + 0.2906vw), 6px)',
      paddingLeft: 'clamp(7.06px, calc(0px + 1.4967vw), 9.41px)',
      paddingRight: 30,
      lineHeight: 1.4,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#999',
      borderRadius: 18,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
  },
  treatmentMenuItem: {
    fontSize: 'clamp(0.66rem, calc(0.291rem + 0.549vw), 0.95rem)',
    marginTop: 0,
    padding: '6px 12px 6px 16px',
    color: '#002D55',
    borderBottom: '1px solid #f0f0f0',
    '&:hover': {
      backgroundColor: '#fb741a !important',
      color: '#fff !important',
    },
    '&.Mui-selected': {
      backgroundColor: '#eaf4ff',
      color: '#002D55',
      '&:hover': {
        backgroundColor: '#fb741a !important',
        color: '#fff !important',
      },
    },
  },
  orangeFocused: {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#002D55',
    },
  },
  textfieldColor: {
    '& .MuiInputBase-input': {
      color: '#002D55',
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#002D55',
      opacity: 1,
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: '#002D55',
      opacity: 1,
    },
  },
  dialogTitle: {
    color: '#002D55',
    fontWeight: 'bold',
    fontSize: 20,
    textTransform: 'uppercase',
    marginBottom: 0,
  },
  dialogContent: {
    color: '#002D55',
    fontWeight: 'bold',
    fontSize: 20,
    paddingTop: 5,
  },
  newBookingButton: {
    transition: 'background 0.2s',
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    maxHeight: 30,
    zIndex: 2,
    border: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      fontWeight: 'bold',
      maxHeight: 30,
      zIndex: 2,
    },
  },
  launcherCloseButton: {
    transition: 'background 0.2s',
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    minHeight: 32,
    minWidth: 90,
    borderRadius: 18,
    padding: '0 16px',
    zIndex: 2,
    border: 'none',
    boxShadow: 'none',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      fontWeight: 'bold',
      boxShadow: 'none',
      zIndex: 2,
    },
  },
  messagesDialog: {
    '& .MuiDialog-paper': {
      maxWidth: '40%',
      height: '90vh',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 14,
      overflow: 'hidden',
    },
  },
  messagesDialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #eee',
    paddingBottom: 0,
  },
  messagesTabsPanel: {
    padding: '12px 0',
    borderBottom: '1px solid #eee',
    flexShrink: 0,
    '& .MuiTab-root': {
      minWidth: 'auto',
      textTransform: 'none',
      fontSize: '14px',
      color: '#666',
      '&.Mui-selected': {
        color: '#002D55',
        fontWeight: 600,
      },
    },
    '& .MuiTabs-indicator': {
      backgroundColor: '#fb741a',
      height: 3,
    },
  },
  messagesListContainer: {
    flex: 1,
    overflowY: 'auto',
    borderRight: '1px solid #eee',
    padding: 0,
  },
  messagesListItem: {
    borderBottom: '1px solid #f0f0f0',
    padding: '12px 16px',
    cursor: 'pointer',
    transition: 'background 0.2s',
    '&:hover': {
      background: '#f5f5f5',
    },
  },
  newMessageForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '16px',
  },
}));

export function PatientHeaderContent({
  onOpenMessage = () => {},
  onLogout = () => window.location.replace('/'),
  onProfile = () => {},
  onOpenVideo = () => {},
  onOpenCheckin = () => {},
  onTreatmentChange = () => {}, // Callback when treatment is selected
}) {
  const classes = useStyles();
  const token = useSelector(state => state.auth.token);
  const user = useSelector(state => state.auth.user);
  const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
  const [anchorEl, setAnchorEl] = React.useState(null);
  const handleAvatarClick = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // Treatment selection state
  const [treatments, setTreatments] = React.useState([]);
  const [selectedTreatmentId, setSelectedTreatmentId] = React.useState('');
  const [loadingTreatments, setLoadingTreatments] = React.useState(false);
  const [nextAppointment, setNextAppointment] = React.useState(null);
  const [loadingNextAppointment, setLoadingNextAppointment] = React.useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = React.useState(0);
  const [followupDue, setFollowupDue] = React.useState(false);
  const [followupDaysSince, setFollowupDaysSince] = React.useState(null);
  const [autopayRequestPending, setAutopayRequestPending] = React.useState(false);
  const [appointmentDialogOpen, setAppointmentDialogOpen] = React.useState(false);
  const [videoLauncherOpen, setVideoLauncherOpen] = React.useState(false);
  const [videoCallOpen, setVideoCallOpen] = React.useState(false);
  const [videoCallAppointment, setVideoCallAppointment] = React.useState(null);
  const [nextVideoAppointment, setNextVideoAppointment] = React.useState(null);
  const [cancellingAppointment, setCancellingAppointment] = React.useState(false);

  // Fetch treatments on mount
  React.useEffect(() => {
    let active = true;

    const fetchTreatments = async () => {
      if (!token) return;
      
      setLoadingTreatments(true);
      try {
        const data = await fetchPatientHeaderData(
          `treatments:${token}`,
          'https://api.virtus-one.com/treatment/user/treatments/',
          token,
        );
        if (!active) return;
        setTreatments(data);

        // Restore from localStorage or default to first treatment
        const savedTreatmentId = localStorage.getItem('vp_selectedTreatmentId');
        if (savedTreatmentId && data.find(t => t.id === savedTreatmentId)) {
          setSelectedTreatmentId(savedTreatmentId);
          onTreatmentChange(data.find(t => t.id === savedTreatmentId));
        } else if (data.length > 0) {
          setSelectedTreatmentId(data[0].id);
          onTreatmentChange(data[0]);
        }
      } catch (error) {
        console.error('[PatientHeader] Failed to fetch treatments:', error);
      } finally {
        if (active) {
          setLoadingTreatments(false);
        }
      }
    };
    
    fetchTreatments();

    return () => {
      active = false;
    };
  }, [token]);

  React.useEffect(() => {
    if (!token) return undefined;

    let active = true;

    const fetchNextAppointment = async (forceRefresh = false) => {
      setLoadingNextAppointment(true);
      try {
        const data = await fetchPatientHeaderData(
          `appointments:${token}`,
          'https://api.virtus-one.com/api/calendar/appointments/',
          token,
          forceRefresh,
        );
        if (active) {
          const appointments = data.appointments || [];
          const now = moment();

          // Get the logged-in user's ID
          const userId = user && (user.id || user.user_id || user.userId);

          const upcoming = appointments
            .filter((appt) => {
              // Filter appointments to only include those for the logged-in user
              if (!userId) return false;
              const apptUserId = appt.patient || appt.patient_id || appt.user_id;
              return apptUserId && String(apptUserId) === String(userId);
            })
            .map((appt) => ({
              ...appt,
              startMoment: appt.start_datetime ? moment(appt.start_datetime) : null,
            }))
            .filter((appt) => appt.startMoment && appt.startMoment.isSameOrAfter(now))
            .sort((a, b) => a.startMoment.valueOf() - b.startMoment.valueOf());

          setNextAppointment(upcoming[0] || null);
          setNextVideoAppointment(upcoming.find((appt) => String(
            appt?.appointment_type
            || appt?.appointment_type_label
            || appt?.service_name
            || '',
          ).toLowerCase().includes('video')) || null);
        }
      } catch (error) {
        console.error('[PatientHeader] Failed to fetch next appointment:', error);
      } finally {
        if (active) {
          setLoadingNextAppointment(false);
        }
      }
    };

    const refreshAppointments = () => {
      fetchNextAppointment(true);
    };

    fetchNextAppointment();
    window.addEventListener('patient:appointments-updated', refreshAppointments);

    return () => {
      active = false;
      window.removeEventListener('patient:appointments-updated', refreshAppointments);
    };
  }, [token, user]);

  const refreshFollowupStatus = React.useCallback(() => {
    const lastFollowup = localStorage.getItem('vp_last_followup_answered_at');
    if (!lastFollowup) {
      setFollowupDue(true);
      setFollowupDaysSince(null);
      return;
    }

    const lastMoment = moment(lastFollowup);
    if (!lastMoment.isValid()) {
      setFollowupDue(true);
      setFollowupDaysSince(null);
      return;
    }

    const daysSince = moment().diff(lastMoment, 'days');
    setFollowupDaysSince(daysSince);
    setFollowupDue(daysSince >= 7);
  }, []);

  React.useEffect(() => {
    refreshFollowupStatus();
    const onFocus = () => refreshFollowupStatus();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshFollowupStatus]);

  React.useEffect(() => {
    const fetchAutopayRequestStatus = async () => {
      if (!token) return;

      try {
        const data = await fetchPatientHeaderData(
          `autopay-request:${token}`,
          `${backendBaseUrl}/api/profile/autopay/request/`,
          token,
        );
        const pendingFromArray = Array.isArray(data)
          ? data.some((item) => item?.pending === true || item?.status === 'pending')
          : false;
        const pendingFromObject = Boolean(
          data?.pending
          || data?.has_pending_request
          || data?.request_pending
          || (Array.isArray(data?.results) && data.results.length > 0)
        );

        setAutopayRequestPending(pendingFromArray || pendingFromObject);
      } catch (error) {
        console.error('[PatientHeader] Failed to fetch autopay request status:', error);
      }
    };

    fetchAutopayRequestStatus();
  }, [backendBaseUrl, token]);

  React.useEffect(() => {
    const fetchUnreadMessages = async () => {
      if (!token) return;

      try {
        const data = await fetchPatientHeaderData(
          `conversations:${token}`,
          `${backendBaseUrl}/api/messaging/conversations/`,
          token,
        );
        const conversations = Array.isArray(data) ? data : [];
        const totalUnread = conversations.reduce((sum, conversation) => (
          sum + Number(conversation?.unread_count || 0)
        ), 0);

        setUnreadMessageCount(totalUnread);
      } catch (error) {
        console.error('[PatientHeader] Failed to fetch unread message count:', error);
      }
    };

    fetchUnreadMessages();
    const intervalId = setInterval(fetchUnreadMessages, 30000);
    window.addEventListener('chat:message-read', fetchUnreadMessages);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('chat:message-read', fetchUnreadMessages);
    };
  }, [backendBaseUrl, token]);

  const handleOpenAutopayRequest = () => {
    handleNotificationsClose();
    window.location.href = '/myaccount?tab=payment&autopay_request=1';
  };

  const getAppointmentIcon = (appointmentType) => {
    if (!appointmentType) return <EventIcon style={{ color: '#002D55' }} />;

    const normalized = String(appointmentType).toLowerCase();
    if (normalized.includes('video')) {
      return <VideoCallIcon style={{ color: '#002D55' }} />;
    }

    return <EventIcon style={{ color: '#002D55' }} />;
  };

  const getAppointmentLabel = (appointmentType) => {
    if (!appointmentType) return 'Appointment';

    const normalized = String(appointmentType).toLowerCase();
    if (normalized.includes('video')) return 'Video Consultation';
    if (normalized.includes('follow')) return 'Follow-up';
    if (normalized.includes('assessment')) return 'Assessment';
    if (normalized.includes('treatment')) return 'Treatment Session';
    if (normalized.includes('in_person')) return 'In-Person';

    return 'Appointment';
  };

  const getAppointmentTitle = (appointment) => (
    appointment?.service_name
    || appointment?.appointment_type_label
    || getAppointmentLabel(appointment?.appointment_type)
    || 'Appointment'
  );

  const getAppointmentSubtitle = (appointment) => (
    appointment?.staff_member_name
    || appointment?.provider_name
    || appointment?.notes
    || appointment?.appointment_type_label
    || appointment?.service_name
    || ''
  );

  const isPastAppointment = (appointment) => {
    if (!appointment?.start_datetime) {
      return false;
    }

    return moment(appointment.start_datetime).isBefore(moment());
  };

  const nextAppointmentTime = nextAppointment?.start_datetime
    ? moment(nextAppointment.start_datetime).format('HH:mm')
    : '';
  const nextAppointmentDate = nextAppointment?.start_datetime
    ? moment(nextAppointment.start_datetime).format('DD/MM')
    : '';
  const nextVideoAppointmentTime = nextVideoAppointment?.start_datetime
    ? moment(nextVideoAppointment.start_datetime).format('HH:mm')
    : '';
  const nextVideoAppointmentDate = nextVideoAppointment?.start_datetime
    ? moment(nextVideoAppointment.start_datetime).format('DD/MM')
    : '';
  const nextAppointmentLabel = nextVideoAppointment
    ? 'Video consultation booked'
    : nextAppointment?.appointment_type
    ? getAppointmentLabel(nextAppointment.appointment_type)
    : 'No appointment';
  const nextAppointmentIcon = nextVideoAppointment
    ? <VideoCallIcon style={{ color: '#002D55' }} />
    : <VideoCallIcon style={{ color: '#002D55' }} />;
  const nextAppointmentIsPast = isPastAppointment(nextAppointment);
  const isVideoBooked = Boolean(nextVideoAppointment);

  const handleOpenVideoLauncher = () => {
    setVideoLauncherOpen(true);
  };

  const handleNextAppointmentClick = () => {
    if (nextVideoAppointment) {
      handleOpenVideoLauncher();
      return;
    }

    if (nextAppointment) {
      setAppointmentDialogOpen(true);
    }
  };

  const handleCancelAppointment = async () => {
    if (!nextAppointment || !token) return;
    if (nextAppointmentIsPast) {
      alert('Past appointments cannot be cancelled.');
      return;
    }
    
    setCancellingAppointment(true);
    try {
      const response = await fetch(`https://api.virtus-one.com/api/calendar/appointments/${nextAppointment.id}/cancel/`, {
        method: 'POST',
        headers: { 
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'cancelled' })
      });
      
      if (response.ok) {
        // Update the appointment locally
        setNextAppointment(prev => prev ? { ...prev, status: 'cancelled' } : null);
        setAppointmentDialogOpen(false);
        // Optionally refresh appointments list
        window.location.reload();
      } else {
        console.error('[PatientHeader] Failed to cancel appointment');
        let errorMessage = 'Failed to cancel appointment. Please try again.';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          console.error('[PatientHeader] Failed to parse cancellation error:', parseError);
        }
        alert(errorMessage);
      }
    } catch (error) {
      console.error('[PatientHeader] Error cancelling appointment:', error);
      alert('Error cancelling appointment. Please try again.');
    } finally {
      setCancellingAppointment(false);
    }
  };

  const handleOpenFollowupAssessment = () => {
    window.location.href = '/FollowUpAssessment_questionaire';
  };

  const handleJoinVideoCall = () => {
    if (warnIfVideoCallAlreadyActive()) {
      return;
    }

    if (nextVideoAppointment) {
      onOpenVideo(nextVideoAppointment);
    } else {
      setVideoCallAppointment(null);
      setVideoCallOpen(true);
    }

    setVideoLauncherOpen(false);
  };

  // Handle treatment selection
  const handleTreatmentSelect = (event) => {
    const treatmentId = event.target.value;
    setSelectedTreatmentId(treatmentId);
    
    // Persist selection
    localStorage.setItem('vp_selectedTreatmentId', treatmentId);
    
    // Notify parent
    const selectedTreatment = treatments.find(t => t.id === treatmentId);
    if (selectedTreatment) {
      onTreatmentChange(selectedTreatment);
    }
  };

  const handleMessagesClick = (event) => {
    window.dispatchEvent(
      new CustomEvent('chat:open-messages', { detail: { anchorEl: event.currentTarget } })
    );
  };

  // notifications menu state
  const [notificationsAnchorEl, setNotificationsAnchorEl] = React.useState(null);
  const handleNotificationsClick = (e) => setNotificationsAnchorEl(e.currentTarget);
  const handleNotificationsClose = () => setNotificationsAnchorEl(null);

  const notifications = [
    ...(autopayRequestPending ? [{
      id: 'autopay-request',
      title: 'Auto-pay request from your physiotherapist',
      subtitle: 'Open Payment & Subscription to accept or decline',
      onClick: handleOpenAutopayRequest,
    }] : []),
    ...(followupDue ? [{
      id: 'followup-due',
      title: 'Click to start check-in',
      subtitle: followupDaysSince !== null
        ? `${followupDaysSince} days since last update`
        : 'Weekly check-in is due',
      onClick: () => {
        handleNotificationsClose();
        handleOpenFollowupAssessment();
      }
    }] : [])
  ];

  const getAppointmentDuration = () => {
    if (!nextAppointment?.start_datetime || !nextAppointment?.end_datetime) return '';
    const start = moment(nextAppointment.start_datetime);
    const end = moment(nextAppointment.end_datetime);
    const duration = moment.duration(end.diff(start));
    const minutes = duration.asMinutes();
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    const remainingMins = minutes % 60;
    return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours} hour${hours > 1 ? 's' : ''}`;
  };

  return (
    <>
      <PatientVideoCallModal
        open={videoCallOpen}
        onClose={() => setVideoCallOpen(false)}
        authToken={token}
        appointment={videoCallAppointment}
        selectedTreatmentId={selectedTreatmentId}
      />

      {/* Appointment Details Dialog */}
      <Dialog
        open={appointmentDialogOpen}
        onClose={() => setAppointmentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography className={classes.dialogTitle}>Appointment Details</Typography>
            {nextAppointment && (
              <Typography variant="caption" style={{ color: '#666' }}>
                ID: {nextAppointment.id.substring(0, 8)}...
              </Typography>
            )}
          </div>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {nextAppointment && (
            <div>
              {nextAppointment.status === 'cancelled' && (
                <div style={{
                  padding: '12px',
                  marginBottom: '16px',
                  background: 'rgba(244, 67, 54, 0.1)',
                  borderLeft: '4px solid #f44336',
                  borderRadius: '4px',
                }}>
                  <Typography variant="body2" style={{ color: '#d32f2f', fontWeight: 600 }}>
                    This appointment has been cancelled
                  </Typography>
                </div>
              )}
              {nextAppointment.status !== 'cancelled' && nextAppointmentIsPast && (
                <div style={{
                  padding: '12px',
                  marginBottom: '16px',
                  background: '#f5f5f5',
                  borderLeft: '4px solid #9e9e9e',
                  borderRadius: '4px',
                }}>
                  <Typography variant="body2" style={{ color: '#666', fontWeight: 600 }}>
                    Past appointments can no longer be cancelled.
                  </Typography>
                </div>
              )}

              <Grid container >
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Service"
                    value={nextAppointmentLabel}
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled
                    InputLabelProps={{ shrink: true }}
                    className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Provider"
                    value={nextAppointment.staff_member_name || 'Staff Member'}
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled
                    InputLabelProps={{ shrink: true }}
                    className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Date"
                    value={nextAppointment.start_datetime ? moment(nextAppointment.start_datetime).format('YYYY-MM-DD') : ''}
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled
                    InputLabelProps={{ shrink: true }}
                    className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Start Time"
                    value={nextAppointment.start_datetime ? moment(nextAppointment.start_datetime).format('HH:mm') : ''}
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled
                    InputLabelProps={{ shrink: true }}
                    className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="End Time"
                    value={nextAppointment.end_datetime ? moment(nextAppointment.end_datetime).format('HH:mm') : ''}
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled
                    InputLabelProps={{ shrink: true }}
                    className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Duration"
                    value={getAppointmentDuration()}
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled
                    InputLabelProps={{ shrink: true }}
                    className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Status"
                    value={nextAppointment.status || 'scheduled'}
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled
                    InputLabelProps={{ shrink: true }}
                    className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    value={nextAppointment.notes || ''}
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled
                    multiline
                    rows={2}
                    InputLabelProps={{ shrink: true }}
                    className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                  />
                </Grid>
              </Grid>

              {nextAppointment.status !== 'cancelled' && !nextAppointmentIsPast && (
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', borderTop: '1px solid #eee', paddingTop: '16px' }}>
                  <Button onClick={() => setAppointmentDialogOpen(false)} className={classes.newBookingButton} variant="contained">
                    Close
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleCancelAppointment}
                    disabled={cancellingAppointment}
                    style={{ background: '#f44336', color: '#fff' }}
                  >
                    {cancellingAppointment ? 'Cancelling...' : 'Cancel Appointment'}
                  </Button>
                </div>
              )}

              {(nextAppointment.status === 'cancelled' || nextAppointmentIsPast) && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #eee', paddingTop: '16px' }}>
                  <Button onClick={() => setAppointmentDialogOpen(false)} className={classes.newBookingButton} variant="contained">
                    Close
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={videoLauncherOpen}
        onClose={() => setVideoLauncherOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 18 } }}
      >
        <DialogTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <VideoCallIcon style={{ color: '#002D55' }} />
            <Typography className={classes.dialogTitle} style={{ color: '#002D55', fontWeight: 700 }}>
              Video call launcher
            </Typography>
          </div>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {nextVideoAppointment ? (
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ padding: 14, borderRadius: 14, border: '1px solid #e0e0e0', background: '#f7faff' }}>
                <Typography style={{ color: '#002D55', fontWeight: 700 }}>
                  {getAppointmentTitle(nextVideoAppointment)}
                </Typography>
                <Typography variant="body2" style={{ color: '#666', marginTop: 6, lineHeight: 1.5 }}>
                  {nextVideoAppointmentDate} {nextVideoAppointmentTime ? `• ${nextVideoAppointmentTime}` : ''}
                </Typography>
                <Typography variant="body2" style={{ color: '#666', marginTop: 8, lineHeight: 1.5 }}>
                  {getAppointmentSubtitle(nextVideoAppointment) || 'Video consultation'}
                </Typography>
              </div>
              <Typography variant="body2" style={{ color: '#666', lineHeight: 1.55 }}>
                Open the videocall when you are ready. If this appointment uses a waiting room, you will join that step first.
              </Typography>
            </div>
          ) : (
            <Typography variant="body2" style={{ color: '#666', lineHeight: 1.55 }}>
              No videocall is currently booked. You can still use this as a guide and test call.
            </Typography>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px 20px', justifyContent: 'space-between' }}>
          <Button onClick={() => setVideoLauncherOpen(false)} className={classes.launcherCloseButton} variant="contained">
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleJoinVideoCall}
            style={{ backgroundColor: '#002D55', color: '#fff', fontWeight: 600, borderRadius: 18, minHeight: 32, textTransform: 'none' }}
          >
            {nextVideoAppointment ? 'Join videocall' : 'Start test call'}
          </Button>
        </DialogActions>
      </Dialog>

      <img src={logo7} alt="Virtus Physio" className={classes.responsiveLogo} />

      {/* Treatment Selector Dropdown */}
      {treatments.length > 1 && !loadingTreatments && (
        <FormControl variant="outlined" size="small" className={classes.treatmentSelect}>
          <Select
            value={selectedTreatmentId}
            onChange={handleTreatmentSelect}
            displayEmpty
            MenuProps={{
              getContentAnchorEl: null,
              classes: { paper: classes.treatmentMenuPaper },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              PaperProps: {
                style: { borderRadius: 18, marginTop: 8, overflow: 'hidden' },
              },
            }}
          >
            {treatments.map((treatment) => (
              <MenuItem 
                key={treatment.id} 
                value={treatment.id}
                className={classes.treatmentMenuItem}
              >
                {treatment.name
                  || treatment.display_name
                  || treatment.diagnosis_title
                  || treatment.diagnosis?.title
                  || treatment.diagnosis?.diagnosis
                  || treatment.diagnosis_identifier
                  || 'Treatment'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <div className={classes.spacer} />

      <Tooltip 
        title={loadingNextAppointment 
          ? 'Loading...' 
          : isVideoBooked
            ? `Video call booked: ${getAppointmentTitle(nextVideoAppointment)} • ${nextVideoAppointmentDate} ${nextVideoAppointmentTime}`
            : nextAppointment 
            ? `Next appointment: ${nextAppointmentLabel} • ${nextAppointmentDate} ${nextAppointmentTime}` 
            : 'Video call guide and test call'
        } 
        arrow
      >
        <Chip
          icon={nextAppointmentIcon}
          label={loadingNextAppointment
            ? 'Next appointment: loading...'
            : isVideoBooked
              ? `Video call booked • ${nextVideoAppointmentDate} ${nextVideoAppointmentTime}`
              : nextAppointment
              ? `Next appointment: ${nextAppointmentLabel} • ${nextAppointmentDate} ${nextAppointmentTime}`
              : 'No appointment'}
          clickable
          onClick={handleNextAppointmentClick}
          className={classes.chip}
          style={{ background: '#eaf4ff', color: '#002D55' }}
        />
      </Tooltip>

      <Tooltip title={isVideoBooked ? 'Open video call launcher' : 'Open guide and test call'} arrow>
        <span>
          <IconButton
            className={classes.smallIconButton}
            onClick={handleOpenVideoLauncher}
            aria-label="Open video call launcher"
          >
            <VideoCallIcon className={classes.headerIcon} style={{ color: '#002D55' }} />
          </IconButton>
        </span>
      </Tooltip>


      <Tooltip title="Notifications" arrow>
        <IconButton className={classes.notificationIconButton} onClick={handleNotificationsClick}>
          <Badge classes={{ badge: classes.customBadge }} badgeContent={notifications.length}>
            <NotificationsIcon className={classes.headerIcon} style={{ color: '#002D55' }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Tooltip title="Messages" arrow>
        <IconButton className={classes.messageIconButton} onClick={handleMessagesClick}>
          <Badge classes={{ badge: classes.customBadge }} badgeContent={unreadMessageCount}>
            <MailOutlineIcon className={classes.headerIcon} style={{ color: '#002D55' }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={notificationsAnchorEl}
        keepMounted
        open={Boolean(notificationsAnchorEl)}
        onClose={handleNotificationsClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: classes.menuPaper, style: { borderRadius: 14 } }}
      >
        <div className={classes.notificationsMenuHeader}>Notifications</div>
        {notifications.map(note => (
          <MenuItem key={note.id} onClick={note.onClick} className={classes.dropdownMenuItem}>
            <ListItemText
              primary={note.title}
              secondary={note.subtitle}
            />
          </MenuItem>
        ))}
        {notifications.length === 0 && <MenuItem disabled className={classes.dropdownMenuItem}>No notifications</MenuItem>}
      </Menu>

      <ChatSystemContainer
        variant="patient"
      />

      <IconButton className={classes.smallIconButton} onClick={handleAvatarClick}>
        <Avatar alt="Profile" className={classes.smallAvatar} style={{ backgroundColor: '#002D55' }}>
          <PersonIcon className={classes.headerIcon} />
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: classes.menuPaper, style: { borderRadius: 14 } }}
      >
        <MenuItem onClick={() => { handleClose(); onProfile(); }} className={classes.dropdownMenuItem}>Profile</MenuItem>
        <MenuItem onClick={() => { handleClose(); onLogout(); }} className={classes.dropdownMenuItem}>Logout</MenuItem>
      </Menu>

    </>
  );
}

export default function HeaderPatient(props) {
  // default wrapper preserves previous usage
  return (
    <AppBar position="static" className={useStyles().appBar} style={{ margin: 0, padding: 0, display: 'flex' }}>
      <Toolbar className={useStyles().toolbar} disableGutters style={{ paddingLeft: 0, paddingRight: 0, margin: 0, marginBottom: 0, paddingTop: 0, paddingBottom: 0 }}>
        <PatientHeaderContent {...props} />
      </Toolbar>
    </AppBar>
  );
}
{/*import React from 'react';
import LandingTabs from './LandingTabs';
import { makeStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import MenuIcon from '@material-ui/icons/Menu';
import logoideas1 from "../images/logoideas1.png"
import logo5 from "../images/logo5.png"
import CssBaseline from "@material-ui/core/CssBaseline";
import { MuiThemeProvider, createMuiTheme } from "@material-ui/core/styles";
import StartButton from "../Buttons/StartButton"
import CustomizedMenus from "./menulist"
import Loginbotton from "./loginbotton"

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(0),
  },
  menuButton: {
    marginRight: theme.spacing(1),
    flex: 0,
    
  },
  title: {
    flexGrow: 1,
    backgroundColor: "#FFFFFF",
    Color: "#002D55",
  },
  responsive: {  
    width: "100%",
    height: "auto",
  },
}));

export default function Header({ tabValue, onTabChange, visible = true }) {
  const classes = useStyles();

  return (
    <div
      className={classes.root}
      style={{
        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: visible ? 'translateY(0)' : 'translateY(-120%)',
        zIndex: 1000,
        position: 'relative',
        top: 0,
        height: "50px"
      }}
    >
      <CssBaseline />
          <AppBar style={{ color: "#002D55", backgroundColor: "#FFFFFF" }} position="absolute" className={clsx(classes.appBar, open && classes.appBarShift)}>
          <Toolbar className={classes.toolbar}>
            <Typography className={classes.title}>
              <img src={logo5} className={classes.responsiveLogo} />
            </Typography>
            <IconButton style={{ color: "#FF7F27", backgroundColor: "#transparent", padding: 0 }}> 
              <Badge badgeContent={6} style={{ color: "#FF7F27", backgroundColor: "#transparent" }}>
                <NotificationsIcon style={{ color: "#002D55", backgroundColor: "#transparent" }} />
              </Badge>
            </IconButton>
            <Button
              aria-controls="customized-menu"
              aria-haspopup="true"
              variant="contained"
              onClick={handleClickLogout}
              style={{backgroundColor: "#002D55", color: "#FFFFFF", marginRight: 10, marginLeft: 30, maxHeight: 30, borderRadius: 8, boxShadow: "0 2px 8px rgba(0,45,85,0.08)"}}
            >Logout

            </Button>
          </Toolbar>
        </AppBar>
        <Drawer
          variant="permanent"
          classes={{
            paper: clsx(classes.drawerPaper, !open && classes.drawerPaperClose),
          }}
          open={open}
        >
          <Grid className={classes.appBar} style={{backgroundColor: "#002D55"}}/>
          <Divider />
          <List style={{marginLeft: open ? -5 : -5}}>
            <ListItem style={{ minHeight: 56, margin: 0, paddingLeft: 3, justifyContent: 'flex-start' }}>
              <IconButton
                onClick={open ? handleDrawerClose : handleDrawerOpen}
              >
                <MenuIcon className={classes.drawerIcon} style={{ color: '#002D55' }} />
              </IconButton>
            </ListItem>
            {React.Children.map(mainListItems.props.children, child =>
              React.cloneElement(child, {
                children: [
                  child.props.children[0], // ListItemIcon
                  open ? child.props.children[1] : null // ListItemText only if open
                ]
              })
            )}
          </List>
          <Divider />
          <List style={{marginLeft: open ? -5 : -5}}>
            {React.Children.map(SecondaryListItems().props.children, child =>
              React.cloneElement(child, {
                children: [
                  child.props.children[0], // ListItemIcon
                  open ? child.props.children[1] : null // ListItemText only if open
                ]
              })
            )}
          </List>
        </Drawer>
    </div>
  );
}








//import { AppBar, Toolbar, Typography } from "@material-ui/core";
//import React from "react";
//
// Standard metode, men den virker ikke, Header() vil ikke render
//export default function Header() {
//  const Header = () => {
//    return (
//      <AppBar position="static" color="primary">
//        <Toolbar>
//          <Typography>
//              Virtus Physio
//          </Typography>
//        </Toolbar>
//      </AppBar>
//    );
//  };
//}
//
//export default function Header() {
//  const displayDesktop = () => {
//    return <Toolbar>{VirtusphysioLogo}</Toolbar>;
//  };
////Hvordan bruger jeg et billede som logo istedet for text
//  const VirtusphysioLogo = (
//    <Typography variant="h6" component="h1">
//      Virtus Physio
//    </Typography>
//  );
//
//  return (
//    <header>
//      <AppBar position="static" color="primary">{displayDesktop()}</AppBar>
//    </header>
//  );
//}*/}